import type { ThemeTokenKey } from "../types/theme";

/**
 * Shared Interaction & Feedback Design Tokens
 */
export const INTERACTIVE_TOKENS = {
  // SINK scale value for pressed states (e.g. 0.95 for subtle physical press)
  SINK_SCALE: 0.95,
  // Opacity overlay hex suffix for accent-based hover/press feedback (approx 15% alpha)
  ACCENT_OVERLAY_ALPHA: "26",
  // Standard high-contrast light color for accent button text (Journal cream)
  ACCENT_CONTRAST: "#fbf9f4",
  // Standard hover/active opacity reduction
  HOVER_OPACITY: 0.8,
} as const;

export const DEFAULT_PATTERN_OPACITY = 10;

export const TOKEN_TO_CSS_MAP = {
  background: "--background",
  foreground: "--foreground",
  surface: "--surface",
  surfaceForeground: "--surface-foreground",
  surfaceSecondary: "--surface-secondary",
  surfaceSecondaryForeground: "--surface-secondary-foreground",
  surfaceTertiary: "--surface-tertiary",
  surfaceTertiaryForeground: "--surface-tertiary-foreground",
  overlay: "--overlay",
  overlayForeground: "--overlay-foreground",
  muted: "--muted",
  default: "--default",
  defaultForeground: "--default-foreground",
  accent: "--accent",
  accentForeground: "--accent-foreground",
  success: "--success",
  successForeground: "--success-foreground",
  warning: "--warning",
  warningForeground: "--warning-foreground",
  danger: "--danger",
  dangerForeground: "--danger-foreground",
  separator: "--separator",
  glassBackground: "--glass-background",
  glassBorder: "--glass-border",
  glassHighlight: "--glass-highlight",
  glow0: "--glow-0",
  glow1: "--glow-1",
  glow2: "--glow-2",
  mutedForeground: "--muted-foreground",
  focus: "--focus",
  link: "--link",
  fieldForeground: "--field-foreground",
  radius: "--radius",
  fontNormal: "--font-normal",
  fontMedium: "--font-medium",
  fontSemiBold: "--font-semibold",
  fontBold: "--font-bold",
  border: "--border",
  patternType: "--pattern-type",
  patternOpacity: "--pattern-opacity",
  isDark: "--is-dark",
  isBlur: "--is-blur",
  themeId: "--theme-id",
  surfaceShadow: "--surface-shadow",
  overlayShadow: "--overlay-shadow",
  fieldShadow: "--field-shadow",
  fieldBorder: "--field-border",
  fieldPlaceholder: "--field-placeholder",
  fieldBackground: "--field-background",
  segment: "--segment",
  segmentForeground: "--segment-foreground",
  primary: "--accent",
  primaryForeground: "--accent-foreground",
  secondary: "--default",
  secondaryForeground: "--default-foreground",
  backdrop: "--backdrop",
} as const;

export const THEME_EXTRA_VARS_MAP: Partial<Record<ThemeTokenKey, string[]>> = {
  background: ["--background", "--color-background"],
  foreground: ["--foreground", "--color-foreground"],
  surface: ["--surface", "--color-card"],
  surfaceForeground: ["--surface-foreground", "--color-card-foreground"],
  surfaceSecondary: ["--surface-secondary", "--color-secondary"],
  surfaceSecondaryForeground: ["--surface-secondary-foreground", "--color-secondary-foreground"],
  surfaceTertiary: ["--surface-tertiary"],
  surfaceTertiaryForeground: ["--surface-tertiary-foreground"],
  overlay: ["--overlay", "--color-popover"],
  overlayForeground: ["--overlay-foreground", "--color-popover-foreground"],
  success: ["--success", "--color-success"],
  successForeground: ["--success-foreground", "--color-success-foreground"],
  warning: ["--warning", "--color-warning"],
  warningForeground: ["--warning-foreground", "--color-warning-foreground"],
  muted: ["--muted", "--color-muted"],
  mutedForeground: ["--muted-foreground", "--color-muted-foreground"],
  default: ["--default"],
  defaultForeground: ["--default-foreground"],
  accent: ["--accent", "--color-primary", "--color-ring"],
  accentForeground: ["--accent-foreground", "--color-primary-foreground"],
  separator: ["--separator", "--border", "--color-border", "--color-input"],
  fieldBackground: ["--field-background"],
  fieldForeground: ["--field-foreground"],
  fieldBorder: ["--field-border"],
  fieldPlaceholder: ["--field-placeholder"],
  fieldShadow: ["--field-shadow"],
  surfaceShadow: ["--surface-shadow"],
  overlayShadow: ["--overlay-shadow"],
  link: ["--link"],
  focus: ["--focus"],
  segment: ["--segment"],
  segmentForeground: ["--segment-foreground"],
  glassBackground: ["--glass-background", "--glass"],
  glassBorder: ["--glass-border"],
  glassHighlight: ["--glass-highlight"],
  glow0: ["--glow-0"],
  glow1: ["--glow-1"],
  glow2: ["--glow-2"],
  radius: ["--radius"],
  fontNormal: ["--font-normal"],
  fontMedium: ["--font-medium"],
  fontSemiBold: ["--font-semibold"],
  fontBold: ["--font-bold"],
  border: ["--border", "--color-border", "--color-input"],
  backdrop: ["--backdrop"],
  danger: ["--danger", "--color-danger", "--color-destructive"],
  dangerForeground: [
    "--danger-foreground",
    "--color-danger-foreground",
    "--color-destructive-foreground",
  ],
};

export type RadiusScale = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "full";
